import { OrderChargeType } from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useCreateChargeMutation } from "Order/Modules/Payment/Api/Mutation/useCreateChargeMutation";
import { ActionButton } from "Order/Modules/Payment/Components/ActionButton";
import {
	type AddChargeFormModel,
	getAddChargeFormModelSchema,
} from "Order/Modules/Payment/Components/Forms/Schema/addChargeFormSchema";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import { FormAutocomplete } from "Shared/Components/Form/CustomFormComponent/FormAutocomplete";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select, {
	type OptionType,
} from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "@mui/icons-material";
import { Grid2, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";

const paymentResource = Resources.base.payment.form;
const selectOptionsResource = Resources.base.payment.selectOptions;

type AddChargeFormProps = {
	orderId: number;
	currentPartnerId: number | null;
	partnerOptions: OptionType[];
};

export function AddChargeForm({
	orderId,
	currentPartnerId,
	partnerOptions,
}: AddChargeFormProps) {
	const { t } = useResource();
	const defaultFormValues = {
		dateCreated: dayjs().toISOString(),
		amount: 0,
		type: OrderChargeType.other,
		partnerId: currentPartnerId,
	};
	const form = useForm<AddChargeFormModel>({
		resolver: zodResolver(getAddChargeFormModelSchema(t)),
		defaultValues: defaultFormValues,
	});

	const {
		control,
		formState: { errors, isDirty },
	} = form;

	const { mutate: createCharge, isPending } = useCreateChargeMutation(orderId);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitCreateCharge = () => {
		handleServerError(createCharge, {
			onSuccess: () => {
				form.reset(defaultFormValues);
			},
		});
	};
	const hasChargeCreatePermission = hasPermission(
		UserPermissions.OrderPaymentModule.create,
	);

	return (
		<Stack width="100%" gap={1}>
			<Grid2 container mb={1} spacing={{ xs: 0, sm: 2 }}>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					<DatePicker
						name="dateCreated"
						label={t(paymentResource.dateCreated)}
						control={control}
						errors={errors}
						disabled
						readOnly
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					<Select
						id="type"
						name="type"
						label={t(paymentResource.type)}
						variant="outlined"
						control={control}
						errors={errors}
						disabled={!hasChargeCreatePermission}
						fullWidth
						options={createSelectOptions(
							OrderChargeType,
							t,
							selectOptionsResource.chargeType,
						)}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
					<Input
						id="amount"
						name="amount"
						label={t(paymentResource.amount)}
						variant="outlined"
						control={control}
						errors={errors}
						disabled={!hasChargeCreatePermission}
						isNumber
						fullWidth
					/>
				</Grid2>
				{partnerOptions.length > 0 && (
					<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
						<FormAutocomplete
							id="partnerId"
							name="partnerId"
							label={t(paymentResource.responsiblePartner)}
							control={control}
							errors={errors}
							disabled={!hasChargeCreatePermission}
							fullWidth
							numeric
							groupBy={(option) => option.group ?? ""}
							options={partnerOptions}
						/>
					</Grid2>
				)}
				<Grid2 size={{ xs: 12, md: 1 }}>
					<ActionButton
						onClick={handleSubmitCreateCharge}
						variant="contained"
						loading={isPending}
						disabled={!hasChargeCreatePermission || !isDirty}
					>
						<Check />
					</ActionButton>
				</Grid2>
			</Grid2>
		</Stack>
	);
}
